/**
 * 一括タグ付与ダイアログ。
 *
 * フロー:
 *   1. ファイル選択 (csv/xlsx) → パース
 *   2. マッチキー (メアド/氏名/両方) の列を選択 (自動検出済み)
 *   3. 判定列 (任意) を選択 + 判定タイプ (boolean True / 値あり)
 *   4. 付与するタグ名を入力
 *   5. 実行 → bulkTagAudience callable
 *
 * 既存配信先にだけタグを追加 (新規作成はしない)。
 * 判定列を指定しないと「全行に対してマッチ判定」、指定すると「列が条件を満たす行のみ」。
 */
import { useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { FileUp, Tag, Upload } from 'lucide-react';
import Papa from 'papaparse';
import { useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { functions } from '@/lib/firebase';

const EMAIL_COLUMN_HINTS = [
  'email',
  'e-mail',
  'mail',
  'email address',
  'mail address',
  'メールアドレス',
  'メール',
  'メアド',
];

const NAME_COLUMN_HINTS = ['name', 'fullname', 'full name', '氏名', '名前', '担当者', '担当'];

type MatchKey = 'email' | 'name' | 'both';
type CriterionType = 'boolean' | 'nonempty';

interface BulkTagResult {
  ok: boolean;
  summary: {
    totalRows: number;
    matchedCriterion: number;
    matchedAudience: number;
    tagged: number;
    notFound: number;
  };
}

const bulkTagAudienceFn = httpsCallable<
  {
    rows: Array<Record<string, string>>;
    tag: string;
    matchKey: MatchKey;
    emailKey?: string;
    nameKey?: string;
    criterionColumn?: string | null;
    criterionType?: CriterionType;
  },
  BulkTagResult
>(functions, 'bulkTagAudience');

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ParsedFile {
  headers: string[];
  rows: Array<Record<string, string>>;
  fileName: string;
}

function autoDetectColumn(headers: string[], hints: string[]): string {
  const lower = headers.map((h) => h.toLowerCase().trim());
  for (const hint of hints) {
    const idx = lower.indexOf(hint.toLowerCase());
    if (idx >= 0) return headers[idx];
  }
  for (const hint of hints) {
    const idx = lower.findIndex((h) => h.includes(hint.toLowerCase()));
    if (idx >= 0) return headers[idx];
  }
  return '';
}

export function BulkTagDialog({ open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState<ParsedFile | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [summary, setSummary] = useState<BulkTagResult['summary'] | null>(null);

  const [tag, setTag] = useState('');
  const [matchKey, setMatchKey] = useState<MatchKey>('both');
  const [emailKey, setEmailKey] = useState('');
  const [nameKey, setNameKey] = useState('');
  const [criterionColumn, setCriterionColumn] = useState<string>('__none__');
  const [criterionType, setCriterionType] = useState<CriterionType>('nonempty');

  const reset = () => {
    setParsed(null);
    setTag('');
    setMatchKey('both');
    setEmailKey('');
    setNameKey('');
    setCriterionColumn('__none__');
    setCriterionType('nonempty');
    setSummary(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const applyParsed = (
    headers: string[],
    rows: Array<Record<string, string>>,
    fileName: string,
  ) => {
    if (headers.length === 0 || rows.length === 0) {
      toast.error('有効なヘッダ／データ行が見つかりません');
      return;
    }
    setParsed({ headers, rows, fileName });
    setEmailKey(autoDetectColumn(headers, EMAIL_COLUMN_HINTS));
    setNameKey(autoDetectColumn(headers, NAME_COLUMN_HINTS));
  };

  const parseXlsx = async (file: File) => {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const sheet = wb.Sheets[wb.SheetNames[0]];
    if (!sheet) {
      toast.error('シートが見つかりません');
      return;
    }
    const raw = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
      header: 1,
      defval: '',
      blankrows: false,
    });
    if (raw.length < 2) {
      toast.error('データ行がありません');
      return;
    }
    const headerRow = (raw[0] as unknown[]).map((c) => String(c ?? '').trim());
    const seen = new Map<string, number>();
    const headers = headerRow.map((h) => {
      if (!h) return '__col_' + seen.size;
      const n = seen.get(h) ?? 0;
      seen.set(h, n + 1);
      return n === 0 ? h : `${h}_${n + 1}`;
    });
    const toStr = (v: unknown): string => {
      if (v == null) return '';
      if (typeof v === 'string') return v;
      if (typeof v === 'number') return String(v);
      if (typeof v === 'boolean') return v ? 'true' : 'false';
      if (v instanceof Date) return v.toISOString();
      return String(v);
    };
    const rows: Array<Record<string, string>> = [];
    for (let i = 1; i < raw.length; i++) {
      const r = raw[i] as unknown[];
      const obj: Record<string, string> = {};
      let hasValue = false;
      for (let j = 0; j < headers.length; j++) {
        const val = toStr(r?.[j]);
        if (val !== '') hasValue = true;
        obj[headers[j]] = val;
      }
      if (hasValue) rows.push(obj);
    }
    applyParsed(headers, rows, file.name);
  };

  const handleFile = async (file: File) => {
    setParsing(true);
    setSummary(null);
    const name = file.name.toLowerCase();
    const isXlsx = name.endsWith('.xlsx') || name.endsWith('.xls');
    try {
      if (isXlsx) {
        await parseXlsx(file);
        setParsing(false);
        return;
      }
      Papa.parse<Record<string, string>>(file, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          setParsing(false);
          if (result.errors.length > 0) {
            toast.error(`CSV パース失敗: ${result.errors[0].message}`);
            return;
          }
          const headers = result.meta.fields ?? [];
          const rows = result.data.filter(
            (r): r is Record<string, string> =>
              r !== null && typeof r === 'object',
          );
          applyParsed(headers, rows, file.name);
        },
        error: (err) => {
          setParsing(false);
          toast.error(`CSV 読み込み失敗: ${err.message}`);
        },
      });
    } catch (err) {
      setParsing(false);
      toast.error(
        `ファイル読み込み失敗: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
  };

  const canSubmit = useMemo(() => {
    if (!parsed) return false;
    if (!tag.trim()) return false;
    if ((matchKey === 'email' || matchKey === 'both') && !emailKey) return false;
    if ((matchKey === 'name' || matchKey === 'both') && !nameKey) return false;
    return true;
  }, [parsed, tag, matchKey, emailKey, nameKey]);

  const handleSubmit = async () => {
    if (!parsed || !canSubmit) return;
    setSubmitting(true);
    try {
      // ペイロード削減: マッチキーと判定列のみに絞ってサーバーに送る
      const neededColumns = new Set<string>();
      if (emailKey) neededColumns.add(emailKey);
      if (nameKey) neededColumns.add(nameKey);
      if (criterionColumn !== '__none__') neededColumns.add(criterionColumn);
      const slimRows = parsed.rows.map((r) => {
        const out: Record<string, string> = {};
        for (const k of neededColumns) {
          if (k in r) out[k] = r[k];
        }
        return out;
      });

      const res = await bulkTagAudienceFn({
        rows: slimRows,
        tag: tag.trim(),
        matchKey,
        emailKey: emailKey || undefined,
        nameKey: nameKey || undefined,
        criterionColumn: criterionColumn !== '__none__' ? criterionColumn : null,
        criterionType,
      });
      setSummary(res.data.summary);
      qc.invalidateQueries({ queryKey: ['mail_contacts'] });
      toast.success(
        `タグ「${tag}」を ${res.data.summary.tagged} 件に付与しました`,
      );
    } catch (err) {
      toast.error(
        `失敗: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="size-4" /> 一括タグ付与
          </DialogTitle>
          <DialogDescription>
            CSV / Excel データを参照して、既存の配信先に一括でタグを付けます。
            メアド or 氏名で既存配信先と一致した行のみが対象 (新規作成はしません)。
          </DialogDescription>
        </DialogHeader>

        {!parsed && (
          <div
            className="border-2 border-dashed border-[var(--border)] rounded-lg p-10 text-center hover:border-[var(--accent)] transition-colors cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const file = e.dataTransfer.files?.[0];
              const n = file?.name.toLowerCase() ?? '';
              if (file && (n.endsWith('.csv') || n.endsWith('.xlsx') || n.endsWith('.xls'))) {
                handleFile(file);
              } else {
                toast.error('.csv / .xlsx / .xls ファイルをドロップしてください');
              }
            }}
          >
            <FileUp className="size-8 mx-auto text-[var(--text-muted)] mb-2" />
            <div className="text-sm text-[var(--text-secondary)]">
              クリックまたはドラッグ＆ドロップで CSV / Excel を選択
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={onFileChange}
              className="hidden"
            />
            {parsing && (
              <div className="flex items-center justify-center gap-2 mt-3 text-xs text-[var(--text-muted)]">
                <Spinner className="size-3" /> パース中…
              </div>
            )}
          </div>
        )}

        {parsed && !summary && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
              <FileUp className="size-3.5" />
              <span className="font-medium text-[var(--text-secondary)]">
                {parsed.fileName}
              </span>
              <span>· {parsed.rows.length} 行 / {parsed.headers.length} 列</span>
              <button
                type="button"
                className="ml-auto text-[var(--accent)] hover:underline"
                onClick={reset}
              >
                別のファイルを選択
              </button>
            </div>

            <div>
              <Label className="text-xs">付与するタグ名 (必須)</Label>
              <Input
                value={tag}
                onChange={(e) => setTag(e.target.value)}
                placeholder="例: 2026年4月キャンペーン購読者"
                className="text-sm"
              />
            </div>

            <div>
              <Label className="text-xs">マッチキー (どの列で既存配信先と紐付けるか)</Label>
              <div className="flex gap-1.5 mt-1">
                {(
                  [
                    { v: 'both', label: 'メアド + 氏名' },
                    { v: 'email', label: 'メアドのみ' },
                    { v: 'name', label: '氏名のみ' },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setMatchKey(opt.v)}
                    className={`px-3 py-1 text-xs rounded border transition-colors ${
                      matchKey === opt.v
                        ? 'border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--accent)]'
                        : 'border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {(matchKey === 'email' || matchKey === 'both') && (
                <div>
                  <Label className="text-xs">メアド列</Label>
                  <Select value={emailKey} onValueChange={setEmailKey}>
                    <SelectTrigger>
                      <SelectValue placeholder="メアド列を選択" />
                    </SelectTrigger>
                    <SelectContent>
                      {parsed.headers.map((h) => (
                        <SelectItem key={h} value={h}>{h}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {(matchKey === 'name' || matchKey === 'both') && (
                <div>
                  <Label className="text-xs">氏名列</Label>
                  <Select value={nameKey} onValueChange={setNameKey}>
                    <SelectTrigger>
                      <SelectValue placeholder="氏名列を選択" />
                    </SelectTrigger>
                    <SelectContent>
                      {parsed.headers.map((h) => (
                        <SelectItem key={h} value={h}>{h}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">判定列 (任意)</Label>
                <Select value={criterionColumn} onValueChange={setCriterionColumn}>
                  <SelectTrigger>
                    <SelectValue placeholder="判定なし (全行対象)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">判定なし (全行対象)</SelectItem>
                    {parsed.headers.map((h) => (
                      <SelectItem key={h} value={h}>{h}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">判定タイプ</Label>
                <Select
                  value={criterionType}
                  onValueChange={(v) => setCriterionType(v as CriterionType)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nonempty">何かしらの値あり</SelectItem>
                    <SelectItem value="boolean">Boolean (True/はい/1)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <p className="text-[10px] text-[var(--text-muted)]">
              判定列を指定すると、その列が条件を満たす行のみを対象にします。
              同名複数の既存配信先がいる場合 (氏名のみ判定) はその行はスキップされます。
            </p>
          </div>
        )}

        {summary && (
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-2">
              <Stat label="入力行数" value={summary.totalRows} />
              <Stat label="判定パス" value={summary.matchedCriterion} accent="accent" />
              <Stat label="既存一致" value={summary.matchedAudience} accent="purple" />
              <Stat label="タグ付与" value={summary.tagged} accent="green" />
            </div>
            <p className="text-[11px] text-[var(--text-muted)]">
              {summary.notFound > 0
                ? `${summary.notFound} 行は既存配信先と一致せずスキップされました (新規作成は行いません)。`
                : 'すべての判定パス行が既存配信先と一致しました。'}
            </p>
          </div>
        )}

        <DialogFooter>
          {summary ? (
            <Button type="button" variant="primary" onClick={() => handleClose(false)}>
              閉じる
            </Button>
          ) : (
            <>
              <Button type="button" variant="ghost" onClick={() => handleClose(false)}>
                キャンセル
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleSubmit}
                disabled={!canSubmit || submitting}
              >
                {submitting ? <Spinner className="size-3 mr-1" /> : <Upload className="size-3.5 mr-1" />}
                タグを一括付与
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: 'accent' | 'purple' | 'green';
}) {
  const color =
    accent === 'accent'
      ? 'text-[var(--accent)]'
      : accent === 'purple'
        ? 'text-[var(--accent-purple)]'
        : accent === 'green'
          ? 'text-[var(--accent-green)]'
          : 'text-[var(--text-primary)]';
  return (
    <div className="p-3 rounded border border-[var(--border)] bg-[var(--bg-elevated)] text-center">
      <div className={`text-xl font-bold ${color}`}>{value}</div>
      <div className="text-[10px] text-[var(--text-muted)] mt-0.5">{label}</div>
    </div>
  );
}
